import { useDraggable } from "@dnd-kit/core";
import { Button, Dropdown, Switch, theme, Tooltip } from "antd";
import { ItemType } from "antd/es/menu/interface";
import type { ContentTopic } from "api/config/chalice-types";
import classNames from "classnames";
import {
  MdAutorenew,
  MdDelete,
  MdDragIndicator,
  MdEdit,
  MdMoreVert,
  MdToggleOn,
} from "react-icons/md";

// Define Topic Actions here as they are specific to the list item
const TOPIC_ACTIONS = [
  {
    key: "edit-topic",
    label: "Edit Topic",
    icon: <MdEdit size={18} />,
    requiresPermProp: "canEditBusiness" as const, // Link to prop name
  },
  {
    key: "toggle-enabled",
    getLabel: (topic: ContentTopic) =>
      topic.enabled ? "Disable Topic" : "Enable Topic",
    icon: <MdToggleOn size={18} />,
    requiresPermProp: "canEditBusiness" as const, // Link to prop name
  },
  {
    key: "generate-posts",
    label: "Generate Posts",
    icon: <MdAutorenew size={18} />,
    requiresPermProp: "canGeneratePosts" as const, // Link to prop name
  },
  {
    key: "delete",
    label: "Delete",
    icon: <MdDelete size={18} />,
    requiresPermProp: "canEditBusiness" as const, // Link to prop name
    danger: true,
  },
];

interface TopicListItemProps {
  topic: ContentTopic;
  canEditBusiness: boolean;
  canGeneratePosts: boolean;
  onTopicAction: (key: string, topic: ContentTopic) => void;
  id: string;
  isBulkEditMode: boolean;
  isSelected: boolean;
  onSelect: () => void;
}

// ADD TopicDisplay helper function definition
const TopicDisplay = ({
  topic,
  enabled,
}: {
  topic: ContentTopic;
  enabled: boolean;
}) => (
  <>
    <div className="font-medium text-sm leading-6 text-antd-colorTextPrimary">
      {topic.title}
    </div>
    <span
      className={classNames("block text-xs leading-5", {
        "text-antd-colorTextSecondary": enabled,
      })}
    >
      {topic.body}
    </span>
  </>
);

export const TopicListItem = ({
  topic,
  canEditBusiness,
  canGeneratePosts,
  onTopicAction,
  id,
  isBulkEditMode,
  isSelected,
  onSelect,
}: TopicListItemProps) => {
  // Generate actions based on passed permissions
  const availableActions = TOPIC_ACTIONS.reduce((prev, action) => {
    const permissions = { canEditBusiness, canGeneratePosts };
    // Only add the action if the user has the permission
    if (permissions[action.requiresPermProp]) {
      prev.push({
        key: action.key,
        label:
          "getLabel" in action && action.getLabel
            ? action.getLabel(topic)
            : action.label,
        icon: action.icon,
        danger: !!action.danger,
      });
    }
    return prev;
  }, [] as ItemType[]);

  const handleAction = (key: string) => {
    onTopicAction(key, topic); // Pass topic back up
  };

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: id,
    data: { topic },
    disabled: isBulkEditMode,
  });

  const { token } = theme.useToken();

  // ADD handleCardClick function definition
  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isBulkEditMode) {
      onSelect();
    } else if (canEditBusiness) {
      e.stopPropagation();
      onTopicAction("edit-topic", topic);
    }
  };

  return (
    <div
      ref={setNodeRef}
      className={classNames(
        "flex items-center gap-3 p-3 rounded-md border bg-antd-colorBgContainer transition-all duration-150",
        {
          "!text-antd-colorTextDisabled bg-antd-colorBgContainerDisabled":
            !topic.enabled && !isBulkEditMode,
          "opacity-50 border-dashed": isDragging,
          "border-antd-colorPrimary border-2 shadow-md": isSelected,
          "border-antd-colorBorder": !isSelected,
          "hover:bg-antd-colorBgContainerHover cursor-pointer":
            canEditBusiness || isBulkEditMode,
          "cursor-default": !canEditBusiness && !isBulkEditMode,
        }
      )}
      onClick={handleCardClick}
    >
      {canEditBusiness && !isBulkEditMode && (
        <Button
          type="text"
          size="small"
          className="cursor-grab active:cursor-grabbing"
          icon={<MdDragIndicator className="text-antd-colorIcon" size={20} />}
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        />
      )}
      <div
        className={classNames(
          "flex-grow min-w-0 [&_*]:text-ellipsis [&_*]:whitespace-nowrap [&_*]:overflow-hidden"
        )}
      >
        {canEditBusiness ? (
          <>{TopicDisplay({ topic, enabled: topic.enabled ?? true })}</>
        ) : (
          <Tooltip
            title={
              <div className="[&_span]:!text-white/75">
                {TopicDisplay({ topic, enabled: topic.enabled ?? true })}
              </div>
            }
            trigger="click"
            mouseEnterDelay={3}
          >
            <div>{TopicDisplay({ topic, enabled: topic.enabled ?? true })}</div>
          </Tooltip>
        )}
      </div>
      {!isBulkEditMode && (
        <div
          className="ml-auto flex items-center gap-1 pl-2"
          onClick={(e) => e.stopPropagation()}
        >
          {canEditBusiness && (
            <Switch
              size="small"
              checked={topic.enabled}
              onChange={(_, event) => {
                event?.stopPropagation();
                handleAction("toggle-enabled");
              }}
            />
          )}
          {(canEditBusiness || canGeneratePosts) &&
            availableActions.length > 0 && (
              <Dropdown
                menu={{
                  items: availableActions,
                  onClick: ({ key, domEvent }) => {
                    domEvent.stopPropagation();
                    handleAction(key);
                  },
                }}
                trigger={["click"]}
              >
                <Button
                  type="text"
                  size="small"
                  icon={
                    <MdMoreVert style={{ color: token.colorIcon }} size={20} />
                  }
                />
              </Dropdown>
            )}
        </div>
      )}
    </div>
  );
};
